import { PublicClientApplication } from '@azure/msal-browser';
import { SSO_CONFIG, getAzureCredentials, isAzureConfigured, getRedirectUri } from '../config.js';
import { getDeviceInfo } from '../utils/device.js';
import { storeTokens, clearTokens } from './auth.js';
import { setLanguage, clearUserLanguage } from '../i18n.js';
import { setSentryUser } from '../sentry.js';

let msalInstance = null;

// Initialize MSAL instance with dynamic Azure credentials
async function initializeMsal(azureCredentials) {
  const redirectUri = getRedirectUri();
  
  // Create a new instance if credentials change or no instance exists
  const newConfig = {
    auth: {
      clientId: azureCredentials.clientId,
      authority: `https://login.microsoftonline.com/${azureCredentials.tenantId}`,
      redirectUri: redirectUri,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false,
    }
  };
  

  
  // Always create a new instance to ensure we have the latest credentials
  msalInstance = new PublicClientApplication(newConfig);
  await msalInstance.initialize();
  
  return msalInstance;
}

// SSO login function
export async function ssoLogin(instance) {
  try {
    // Fetch Azure credentials from backend
    const azureCredentials = await getAzureCredentials(instance);
    
    // Initialize MSAL with fetched credentials
    const msal = await initializeMsal(azureCredentials);
    
    // Create login request
    const loginRequest = {
      scopes: SSO_CONFIG.scopes,
      prompt: 'select_account' // Always show account selection
    };
    
    // Trigger Microsoft login popup
    const result = await msal.loginPopup(loginRequest);
    
    if (!result.accessToken) {
      throw new Error('No access token received from Microsoft');
    }
    
    // Get device information
    const device = await getDeviceInfo(false);
    
    // Send token to backend for validation and user session creation
    const url = `https://${instance}.assistgo.de/api/user/sso/microsoft`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        token: result.accessToken,
        device: device
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();

      if (errorData.requiresTwoFactor || errorData.requiresTwoFactorSetup) {
        const twoFactorError = new Error('Two-factor authentication required');
        twoFactorError.twoFactor = {
          requiresTwoFactor: !!errorData.requiresTwoFactor,
          requiresTwoFactorSetup: !!errorData.requiresTwoFactorSetup,
          jwt: errorData.jwt,
          instance,
        };
        throw twoFactorError;
      }

      throw new Error(errorData.message || 'SSO authentication failed');
    }
    
    const data = await response.json();

    if (data.requiresTwoFactorSetup || data.requiresTwoFactor) {
      const twoFactorError = new Error('Two-factor authentication required');
      twoFactorError.twoFactor = {
        requiresTwoFactor: !!data.requiresTwoFactor,
        requiresTwoFactorSetup: !!data.requiresTwoFactorSetup,
        jwt: data.jwt,
        instance,
      };
      throw twoFactorError;
    }

    const jwt = data.jwt || data.token || data.accessToken;
    
    if (!jwt) {
      throw new Error('No JWT token received from backend');
    }
    
    // Create user object from the response data (excluding jwt field)
    const user = {
      _id: data._id,
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      blocked: data.blocked,
      canBulkEdit: data.canBulkEdit,
      canDownload: data.canDownload,
      canImport: data.canImport,
      canManageWorkflows: data.canManageWorkflows,
      canWriteToGlobal: data.canWriteToGlobal,
      directManagers: data.directManagers,
      employees: data.employees,
      image: data.image
    };
    
    const INSTANCE_KEY = 'instance_name';
    const USER_EMAIL_KEY = 'user_email';
    
    storeTokens(jwt, data.refreshToken);
    localStorage.setItem(INSTANCE_KEY, instance);
    
    // Extract email from the backend response (preferred) or Microsoft account
    let email = null;
    if (user && user.email) {
      email = user.email;
      localStorage.setItem(USER_EMAIL_KEY, user.email);
    } else if (result.account && result.account.username) {
      email = result.account.username;
      localStorage.setItem(USER_EMAIL_KEY, result.account.username);
    }

    setSentryUser({ email, instance });

    const lang = data.user?.language || data.language;
    if (lang) {
      setLanguage(lang);
    }

    return {
      success: true,
      user: user,
      jwt: jwt
    };
    
  } catch (error) {
    if (error.twoFactor) throw error;

    clearTokens();
    clearUserLanguage();

    if (error.name === 'BrowserAuthError' && error.errorCode === 'popup_window_error') {
      throw new Error('Login popup was blocked. Please enable popups and try again.');
    } else if (error.name === 'BrowserAuthError' && error.errorCode === 'user_cancelled') {
      throw new Error('Login was cancelled by user.');
    } else if (error.name === 'InteractionRequiredAuthError') {
      throw new Error('Additional authentication required. Please try again.');
    }
    
    throw error;
  }
}

// Check if SSO is configured properly (now checks if we have cached credentials)
export function isSsoConfigured() {
  return isAzureConfigured();
}

// Async version to check if SSO can be configured for a given instance
export async function canUseSso(instance) {
  try {
    await getAzureCredentials(instance);
    return true;
  } catch (error) {
    return false;
  }
}

// Get current Microsoft account (if logged in)
export async function getCurrentMicrosoftAccount() {
  try {
    if (!msalInstance) {
      return null;
    }
    
    const accounts = msalInstance.getAllAccounts();
    return accounts.length > 0 ? accounts[0] : null;
  } catch (error) {
    return null;
  }
}

// Silent SSO (attempt to get token without user interaction)
export async function silentSsoLogin(instance) {
  try {
    // Fetch Azure credentials first
    const azureCredentials = await getAzureCredentials(instance);
    
    if (!msalInstance) {
      // Initialize if not already done
      await initializeMsal(azureCredentials);
    }
    
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      throw new Error('No accounts found');
    }
    
    const silentRequest = {
      scopes: SSO_CONFIG.scopes,
      account: accounts[0]
    };
    
    const result = await msalInstance.acquireTokenSilent(silentRequest);
    
    if (!result.accessToken) {
      throw new Error('No access token received');
    }
    
    // Same backend call as regular SSO
    const device = await getDeviceInfo(false);
    const url = `https://${instance}.assistgo.de/api/user/sso/microsoft`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        token: result.accessToken,
        device: device
      }),
    });
    
    if (!response.ok) {
      throw new Error('Silent SSO failed');
    }
    
    const data = await response.json();

    if (data.requiresTwoFactorSetup || data.requiresTwoFactor) {
      const twoFactorError = new Error('Two-factor authentication required');
      twoFactorError.twoFactor = {
        requiresTwoFactor: !!data.requiresTwoFactor,
        requiresTwoFactorSetup: !!data.requiresTwoFactorSetup,
        jwt: data.jwt,
        instance,
      };
      throw twoFactorError;
    }

    if (!data.success || !data.jwt) {
      throw new Error('Silent SSO failed');
    }

    storeTokens(data.jwt, data.refreshToken);
    localStorage.setItem('instance_name', instance);
    setSentryUser({
      email: data.email || data.user?.email || localStorage.getItem('user_email'),
      instance,
    });
    
    return data;
    
  } catch (error) {
    throw error;
  }
}
